/**
 * SubCategoryAddEditPageV2 — V2 edit-page chrome for Subcategories.
 * Same sticky header recipe as the other V2 edit pages.
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import AddEditSubCategoryV2 from '../../AddEditSubCategory/v2/AddEditSubCategoryV2';
import { Button, cn } from '../../redesign';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function SubCategoryAddEditPageV2() {
  const location = useLocation();
  const params = useParams();
  const isEdit = location.pathname.includes('edit');

  // The form fires the only GET /subcategories/{id} call (via the
  // fetchOneSubcategory Redux action) and hands the title +
  // updated_at back via `onLoaded`. Memoised so the form's effect
  // doesn't re-fire when this page re-renders.
  const [subTitle, setSubTitle] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ subcategory_title, subcategory_updated_at }) => {
    setSubTitle(subcategory_title || null);
    setLastSavedAt(subcategory_updated_at || null);
  }, []);

  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  const eyebrow = isEdit ? 'Editing subcategory' : 'New subcategory';
  const title = isEdit
    ? (subTitle || `Subcategory #${params.id}`)
    : 'Untitled subcategory';

  return (
    <div className="bg-bg min-h-full">
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !subTitle && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div
              className="font-mono text-text-muted"
              style={{ fontSize: 12 }}
            >
              {isEdit ? (
                <>
                  subcategory_id · <strong className="text-navy">{params.id}</strong>
                  {lastSavedLabel && (
                    <>
                      {' · '}
                      <span>{lastSavedLabel}</span>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/subcategories" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="subcategory-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditSubCategoryV2
          isEdit={isEdit}
          selectedId={params.id}
          onLoaded={handleFormLoaded}
        />
      </div>
    </div>
  );
}
